import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Skeleton, Stack } from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { colors } from 'styles/colors';
import { hexWithOpacity } from 'common/utils';
import BellIcon from 'icons/BellIcon';
import NotificationEntry from '../NotificationEntry/NotificationEntry';
import { useGetNotifications, useMarkNotificationsChecked } from 'hooks/useNotifications';
import { emitRefetch } from 'services/appNotificationsService';

const PAGE_SIZE = 10;

function NotificationEntrySkeleton() {
  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Skeleton variant="circular" width={38} height={38} />
          <Box flex={1}>
            <Skeleton variant="text" width="40%" height={22} />
            <Skeleton variant="text" width="70%" height={18} />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function NotificationsList() {
  const [page, setPage] = useState(0);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  // Set atomically alongside notifications inside onSuccess, instead of relying on the query's
  // own isLoading flag - that updates via react-query's internals on a different tick than our
  // local state, which can race with notifications and briefly show the empty state.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const { isFetching } = useGetNotifications(page, PAGE_SIZE, {
    onSuccess: (data: any) => {
      const results = data?.data?.results ?? [];
      setTotalCount(data?.data?.count ?? 0);
      setNotifications((prev) => (page === 0 ? results : [...prev, ...results]));
      setHasLoadedOnce(true);
    },
  });

  const { mutate: markChecked } = useMarkNotificationsChecked(
    () => emitRefetch('refetchNotificationsCount'),
    () => {},
  );

  useEffect(() => {
    markChecked();
    // Only mark as checked once, when the notifications screen is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasMore = notifications.length < totalCount;

  // Updates the item in place instead of refetching, so accumulated pages
  // beyond the first one aren't discarded by a single mark-as-read.
  const handleRead = (notificationId: number) => {
    setNotifications((prev) =>
      prev.map((item) => (item.id === notificationId ? { ...item, read_flag: true } : item))
    );
  };

  if (!hasLoadedOnce) {
    return (
      <Stack spacing={1.5}>
        {Array.from({ length: 5 }).map((_, i) => (
          <NotificationEntrySkeleton key={i} />
        ))}
      </Stack>
    );
  }

  if (notifications.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        textAlign="center"
        gap={2}
        minHeight={'80vh'}
      >
        <Box height={100}>
          <BellIcon
            size={100}
            color={hexWithOpacity(colors.secondary, 0.4)}
          />
        </Box>
        <CustomText text="You're all caught up!" fontWeight={600} color={colors.secondary} />
        <CustomText text="New notifications will appear here when you receive them" color={colors.secondary} />
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      {notifications.map((item: any) => (
        <NotificationEntry key={item.id} item={item} onRead={() => handleRead(item.id)} />
      ))}

      {hasMore && (
        <Box display="flex" justifyContent="center" mt={1}>
          <CustomButton
            text="Load More"
            variant="outlined"
            onClick={() => setPage((prev) => prev + 1)}
            isLoading={isFetching}
            disabled={isFetching}
            height={38}
          />
        </Box>
      )}
    </Stack>
  );
}
